// "Get ready" checklist: the details that make features work, with what
// each one is for and a tap straight to where it's filled in.

import { CheckCircle2, ChevronRight, CircleDashed, ListChecks, Smartphone } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../../data/api.ts';
import { useCollection, useHousehold, useProfile, useSyncState } from '../../data/hooks.ts';
import { readiness } from '../../domain/readiness.ts';
import { navigate } from '../../app/router.ts';
import { Card, CardHeader, ProgressBar } from '../../ui/primitives.tsx';

/** `limit`: show only the next few things to do, with the rest a tap away. */
export function ReadinessCard({ hideWhenDone = false, className = '', limit }: { hideWhenDone?: boolean; className?: string; limit?: number }) {
  const profile = useProfile();
  const household = useHousehold();
  const contacts = useCollection('contacts');
  const sync = useSyncState();
  const [server, setServer] = useState<{ pushPeople: string[]; calendar: boolean } | undefined>();
  const [showDone, setShowDone] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const synced = sync.kind === 'synced';
  useEffect(() => {
    if (synced) void api<{ pushPeople: string[]; calendar: boolean }>('/api/readiness').then(setServer).catch(() => undefined);
  }, [synced]);

  const items = useMemo(() => readiness({ profile, household, contacts, synced, pushPeople: server?.pushPeople, calendar: server?.calendar }), [profile, household, contacts, synced, server]);
  const done = items.filter((i) => i.done);
  const todo = items.filter((i) => !i.done);
  if (hideWhenDone && !todo.length) return null;

  return (
    <Card className={`readiness ${className}`}>
      <CardHeader
        title={todo.length ? 'Get ready' : 'All set'}
        icon={ListChecks}
        sub={todo.length ? `${done.length} of ${items.length} done. Each makes something work — skip any you don’t need.` : 'Everything’s filled in.'}
      />
      <ProgressBar value={done.length / items.length} tone={todo.length ? 'accent' : 'good'} label="Set-up progress" />
      <ul className="readiness-list">
        {(limit && !showAll ? todo.slice(0, limit) : todo).map((i) => (
          <li key={i.id}>
            <button type="button" onClick={() => navigate(i.route)}>
              <CircleDashed aria-hidden className="readiness-icon" />
              <span>
                <strong>{i.label}</strong>
                <span className="small muted">
                  For {i.unlocks}.{i.perPhone ? ' On each phone.' : ''}
                </span>
              </span>
              {i.perPhone ? <Smartphone aria-hidden className="readiness-go" /> : <ChevronRight aria-hidden className="readiness-go" />}
            </button>
          </li>
        ))}
        {showDone &&
          done.map((i) => (
            <li key={i.id} className="done">
              <button type="button" onClick={() => navigate(i.route)}>
                <CheckCircle2 aria-hidden className="readiness-icon" />
                <span>
                  <strong>{i.label}</strong>
                </span>
                <ChevronRight aria-hidden className="readiness-go" />
              </button>
            </li>
          ))}
      </ul>
      <div className="row gap">
        {limit && todo.length > limit && (
          <button type="button" className="link small" onClick={() => setShowAll(!showAll)}>
            {showAll ? 'Show fewer' : `${todo.length - limit} more`}
          </button>
        )}
        {done.length > 0 && (
          <button type="button" className="link small" onClick={() => setShowDone(!showDone)}>
            {showDone ? 'Hide what’s done' : `Show ${done.length} done`}
          </button>
        )}
      </div>
    </Card>
  );
}
